import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Button, Card, IconButton, List, ListItem, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';

import { IconArrowLeft, IconGripVertical, IconRefresh } from '@tabler/icons-react';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { useHistory } from 'react-router-dom';

import api from '../../../../../views/services/api';
import { utilities } from '../../../../../menu-items/utilities';

const mergeOrder = (children, savedOrder) => {
    if (!savedOrder || !savedOrder.length) {
        return [...children];
    }

    const byId = new Map(children.map((child) => [child.id, child]));
    const ordered = savedOrder.map((id) => byId.get(id)).filter(Boolean);
    const orderedIds = new Set(ordered.map((child) => child.id));
    const missing = children.filter((child) => !orderedIds.has(child.id));

    return [...ordered, ...missing];
};

const ReorganizarMenu = () => {
    const theme = useTheme();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [items, setItems] = useState(utilities.children);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);

    const loadOrder = useCallback(async () => {
        try {
            const res = await api.get('v1/apps/default/');
            const app = res.data?.app || {};
            setAppId(app.id || null);
            setItems(mergeOrder(utilities.children, app.menu_order));
        } catch (err) {
            console.error(err);
            setItems(utilities.children);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    const persistOrder = async (newItems, currentAppId) => {
        if (!currentAppId) return;

        try {
            await api.patch(`v1/apps/${currentAppId}/`, { menu_order: newItems.map((item) => item.id) });
            setStatus({ type: 'success', message: 'Ordem do menu salva com sucesso!' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao salvar a ordem do menu.' });
            loadOrder();
        }
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || destination.index === source.index) return;

        const newItems = Array.from(items);
        const [moved] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, moved);

        setItems(newItems);
        persistOrder(newItems, appId);
    };

    const handleReset = async () => {
        if (!appId) return;

        setItems(utilities.children);

        try {
            await api.patch(`v1/apps/${appId}/`, { menu_order: [] });
            setStatus({ type: 'success', message: 'Ordem padrão restaurada!' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao restaurar a ordem padrão.' });
            loadOrder();
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <IconButton onClick={() => history.push('/adm/config/')}>
                    <IconArrowLeft />
                </IconButton>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>
                        Reorganizar Menu
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Arraste os itens para definir a ordem que aparecem no menu lateral "Painel".
                    </Typography>
                </Box>
            </Box>

            {status && (
                <Alert severity={status.type} sx={{ mb: 2, maxWidth: 520 }} onClose={() => setStatus(null)}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ borderRadius: '20px', p: 2, maxWidth: 520 }}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="painel-menu-order">
                        {(provided) => (
                            <List ref={provided.innerRef} {...provided.droppableProps} disablePadding>
                                {items.map((item, index) => {
                                    const Icon = item.icon;

                                    return (
                                        <Draggable draggableId={item.id} index={index} key={item.id}>
                                            {(provided2) => (
                                                <ListItem
                                                    ref={provided2.innerRef}
                                                    {...provided2.draggableProps}
                                                    {...provided2.dragHandleProps}
                                                    sx={{
                                                        mb: 1,
                                                        borderRadius: '12px',
                                                        border:
                                                            theme.palette.mode === 'dark'
                                                                ? '1px solid rgba(255,255,255,0.08)'
                                                                : '1px solid rgba(15,23,42,0.08)',
                                                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff',
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    <IconGripVertical size={18} style={{ marginRight: 12, opacity: 0.5, flexShrink: 0 }} />
                                                    {Icon && (
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Icon size={20} />
                                                        </ListItemIcon>
                                                    )}
                                                    <ListItemText primary={item.title} />
                                                </ListItem>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </List>
                        )}
                    </Droppable>
                </DragDropContext>
            </Card>

            <Button onClick={handleReset} startIcon={<IconRefresh size={18} />} disabled={loading || !appId} sx={{ mt: 2 }}>
                Restaurar ordem padrão
            </Button>
        </Box>
    );
};

export default ReorganizarMenu;
